import { useState } from 'react'
import { useForm, type UseFormRegisterReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { authAPI } from '@/api/client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { logger } from '@/lib/logger'
import { getApiError } from '@/lib/utils'

interface ChangePasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password'),
  newPassword: z.string().min(12, 'New password must be at least 12 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'New passwords do not match',
  path: ['confirmPassword'],
})

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>

function PasswordField({
  id,
  label,
  placeholder,
  field,
  error,
}: {
  id: string
  label: string
  placeholder: string
  field: UseFormRegisterReturn
  error?: string
}) {
  const [reveal, setReveal] = useState(false)
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={reveal ? 'text' : 'password'}
          placeholder={placeholder}
          {...field}
          className={error ? 'border-destructive pr-10' : 'pr-10'}
        />
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          tabIndex={-1}
          aria-label={`${reveal ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
        >
          {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  })

  const changePassword = useMutation({
    mutationFn: (data: ChangePasswordFormData) =>
      authAPI.changePassword(data.currentPassword, data.newPassword, data.confirmPassword),
    onSuccess: () => {
      logger.success('ChangePasswordDialog', 'Password changed successfully')
      reset()
      onOpenChange(false)
    },
  })

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset()
      changePassword.reset()
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => changePassword.mutate(data))}
          className="space-y-4 pt-2"
        >
          <PasswordField
            id="current-password"
            label="Current Password"
            placeholder="Enter current password"
            field={register('currentPassword')}
            error={errors.currentPassword?.message}
          />
          <PasswordField
            id="new-password"
            label="New Password"
            placeholder="At least 12 characters"
            field={register('newPassword')}
            error={errors.newPassword?.message}
          />
          <PasswordField
            id="confirm-password"
            label="Confirm New Password"
            placeholder="Re-enter new password"
            field={register('confirmPassword')}
            error={errors.confirmPassword?.message}
          />

          {changePassword.isError && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {getApiError(changePassword.error, 'Failed to change password. Please try again.')}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={changePassword.isPending}>
              {changePassword.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Change Password
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
